import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import BarcodeScanner from './BarcodeScanner';
import StockManagement from './StockManagement';
import PackagedOrders from './PackagedOrders';
import OrderScanner from './OrderScanner';
import BasketManagement from './BasketManagement';

const Dashboard = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [currentPage, setCurrentPage] = useState('orders');
  const { logout, currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (currentPage === 'orders') {
      fetchOrders();
    }
  }, [currentPage]);

  const getTimeAgo = (timestamp) => {
    if (!timestamp) return 'N/A';

    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    const intervals = {
      year: 31536000,
      month: 2592000,
      week: 604800,
      day: 86400,
      hour: 3600,
      minute: 60
    };

    for (const [unit, secondsInUnit] of Object.entries(intervals)) {
      const interval = Math.floor(seconds / secondsInUnit);
      if (interval >= 1) {
        return `${interval} ${unit}${interval > 1 ? 's' : ''} ago`;
      }
    }

    return 'Just now';
  };

  const isOrderOlderThan6Days = (timestamp) => {
    if (!timestamp) return false;

    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const daysDifference = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    return daysDifference > 6;
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const ordersRef = collection(db, 'orders');

      const q = query(
        ordersRef,
        where('SteadFastHandOver', '==', true),
        where('status', '==', 'Handed over to Courier')
      );

      const querySnapshot = await getDocs(q);
      const ordersData = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();

        if (data.packagingComplete === true) {
          return;
        }

        if (data.ready_for_pickup === true) {
          return;
        }

        ordersData.push({
          id: doc.id,
          ...data
        });
      });

      ordersData.sort((a, b) => {
        const dateA = a.created_at?.toDate ? a.created_at.toDate() : new Date(a.created_at || 0);
        const dateB = b.created_at?.toDate ? b.created_at.toDate() : new Date(b.created_at || 0);
        return dateB - dateA;
      });

      setOrders(ordersData);
    } catch (error) {
      console.error('Error fetching orders:', error);
      alert('Failed to fetch orders: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  const handleProcessOrder = (order) => {
    setSelectedOrder(order);
  };

  const handleCloseScanner = () => {
    setSelectedOrder(null);
    fetchOrders();
  };

  if (loading && currentPage === 'orders') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-2xl font-semibold text-gray-700">Loading orders...</div>
      </div>
    );
  }

  if (selectedOrder) {
    return (
      <BarcodeScanner
        order={selectedOrder}
        onClose={handleCloseScanner}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Sidebar Navigation */}
      <div className="w-64 bg-gray-800 text-white flex flex-col">
        <div className="p-6 border-b border-gray-700">
          <h1 className="text-xl font-bold">HowTech </h1>
          <p className="text-xs text-gray-400 mt-1 truncate">{currentUser?.email}</p>
        </div>

        <nav className="flex-1 p-4">
          <button
            onClick={() => setCurrentPage('orders')}
            className={`w-full text-left px-4 py-3 rounded-lg mb-2 transition duration-200 flex items-center gap-3 ${currentPage === 'orders'
              ? 'bg-blue-600 text-white'
              : 'text-gray-300 hover:bg-gray-700'
              }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <span className="font-medium">Order Packaging</span>
          </button>

          <button
            onClick={() => setCurrentPage('packaged')}
            className={`w-full text-left px-4 py-3 rounded-lg mb-2 transition duration-200 flex items-center gap-3 ${currentPage === 'packaged'
              ? 'bg-blue-600 text-white'
              : 'text-gray-300 hover:bg-gray-700'
              }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
            </svg>
            <span className="font-medium">Packaged Orders</span>
          </button>

          <button
            onClick={() => setCurrentPage('scanner')}
            className={`w-full text-left px-4 py-3 rounded-lg mb-2 transition duration-200 flex items-center gap-3 ${currentPage === 'scanner'
              ? 'bg-blue-600 text-white'
              : 'text-gray-300 hover:bg-gray-700'
              }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
            </svg>
            <span className="font-medium">Order Scanner</span>
          </button>

          <button
            onClick={() => setCurrentPage('basket')}
            className={`w-full text-left px-4 py-3 rounded-lg mb-2 transition duration-200 flex items-center gap-3 ${currentPage === 'basket'
              ? 'bg-blue-600 text-white'
              : 'text-gray-300 hover:bg-gray-700'
              }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <span className="font-medium">Basket Management</span>
          </button>

          <button
            onClick={() => setCurrentPage('stock')}
            className={`w-full text-left px-4 py-3 rounded-lg transition duration-200 flex items-center gap-3 ${currentPage === 'stock'
              ? 'bg-blue-600 text-white'
              : 'text-gray-300 hover:bg-gray-700'
              }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
            <span className="font-medium">Stock Management</span>
          </button>
        </nav>

        <div className="p-4 border-t border-gray-700">
          <button
            onClick={handleLogout}
            className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-lg transition duration-200 flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Logout
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-auto">
        {currentPage === 'orders' ? (
          <>
            <div className="bg-white shadow-md">
              <div className="px-8 py-6">
                <h2 className="text-2xl font-bold text-gray-800">Order Packaging</h2>
                <p className="text-sm text-gray-600 mt-1">Process orders ready for courier delivery</p>
              </div>
            </div>

            <div className="px-8 py-6">
              <div className="mb-6 flex justify-between items-center">
                <h3 className="text-xl font-semibold text-gray-800">
                  Orders Ready for Packaging ({orders.length})
                </h3>
                <button
                  onClick={fetchOrders}
                  className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
                >
                  Refresh
                </button>
              </div>

              {orders.length === 0 ? (
                <div className="bg-white rounded-lg shadow p-12 text-center">
                  <p className="text-gray-600 text-lg">No orders available for packaging</p>
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Customer
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Order ID
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Products
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Time
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {orders.map((order) => {
                        const isOld = isOrderOlderThan6Days(order.created_at);
                        return (
                          <tr
                            key={order.id}
                            className={`${isOld ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-gray-50'}`}
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className={`text-sm font-medium ${isOld ? 'text-red-900' : 'text-gray-900'}`}>
                                {order.customerInfo?.name || 'N/A'}
                              </div>
                              <div className={`text-sm ${isOld ? 'text-red-700' : 'text-gray-500'}`}>
                                {order.customerInfo?.phone || 'N/A'}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className={`text-sm font-mono ${isOld ? 'text-red-900 font-semibold' : 'text-gray-900'}`}>
                                {order.orderId || order.id}
                              </div>
                              <div className={`text-xs ${isOld ? 'text-red-700' : 'text-gray-500'}`}>
                                {order.steadfastConsignmentId || 'N/A'}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex flex-col gap-2">
                                {order.cartItems?.map((item, index) => (
                                  <div key={index} className="flex items-center gap-3">
                                    <img
                                      src={item.image}
                                      alt={item.name}
                                      className="w-12 h-12 object-cover rounded"
                                      onError={(e) => {
                                        e.target.src = 'https://via.placeholder.com/48';
                                      }}
                                    />
                                    <div className="flex-1">
                                      <div className={`text-sm line-clamp-1 ${isOld ? 'text-red-900' : 'text-gray-900'}`}>
                                        {item.name}
                                      </div>
                                      <div className={`text-xs ${isOld ? 'text-red-700' : 'text-gray-500'}`}>
                                        Qty: {item.quantity}
                                        {item.selectedColor && (
                                          <span className="ml-2">
                                            | Color: <span className="font-semibold">
                                              {typeof item.selectedColor === 'object'
                                                ? item.selectedColor.name
                                                : item.selectedColor}  {/* string হলে সরাসরি দেখাবে */}
                                            </span>
                                          </span>
                                        )}
                                        {item.selectedSize && (
                                          <span className="ml-2">| Size: {item.selectedSize}</span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className={`text-sm font-semibold ${isOld ? 'text-red-700' : 'text-gray-600'}`}>
                                {getTimeAgo(order.created_at)}
                              </div>
                              {isOld && (
                                <div className="text-xs text-red-600 font-bold mt-1">
                                  ⚠️ OLD ORDER
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <button
                                onClick={() => handleProcessOrder(order)}
                                className={`${isOld
                                  ? 'bg-red-600 hover:bg-red-700'
                                  : 'bg-green-500 hover:bg-green-600'
                                  } text-white font-semibold py-2 px-4 rounded-lg transition duration-200`}
                              >
                                Process Order
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        ) : currentPage === 'packaged' ? (
          <PackagedOrders />
        ) : currentPage === 'scanner' ? (
          <OrderScanner />
        ) : currentPage === 'basket' ? (
          <BasketManagement />
        ) : (
          <StockManagement />
        )}
      </div>
    </div>
  );
};

export default Dashboard;